import { tracking } from '@iwoca/frontend-tracking-library';
import { driver } from 'driver.js';
import { useNavigate } from 'react-router-dom';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { PATH_NAMES } from 'utils/routes/pathNames';

const trackClick = (tourName: string, buttonText: string) => {
  tracking.ampli.ctaClick({
    ...getAmplitudeDefaultEventProperties(),
    'CTA text': buttonText,
    category: tourName,
  });
};

export const useTutorials = () => {
  const navigate = useNavigate();

  const triggerNavigationTour = () => {
    const navigationTour = driver({
      showProgress: true,
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps: [
        {
          element: '#nav-ctas-left',
          popover: {
            title: '🚀 Welcome to the Portal',
            description: `<p>You can always access the <strong>Dashboard</strong> to manage applications, explore <strong>Products</strong> we offer, and use the <strong>Calculator</strong> for quick estimates.</p><p>Click <strong>“Create a new app”</strong> anytime to start a new application.</p>`,
            side: 'bottom',
            align: 'center',
            nextBtnText: 'Next',
            onNextClick: () => {
              navigationTour.moveNext();
              trackClick('Navigation', 'Next');
            },
          },
          disableActiveInteraction: true,
        },
        {
          element: '#nav-ctas-right',
          popover: {
            title: '💡 Helpful tools and support',
            description: `<p>Stay up to date with <strong>What's New</strong>, find quick answers in <strong>FAQs</strong>, or click your profile icon to invite colleagues and relaunch tours.</p><p>These tools are here to support you as you create applications.</p>`,
            nextBtnText: 'Got it',
            side: 'bottom',
            align: 'center',
            onNextClick: () => {
              navigationTour.destroy();
              trackClick('Navigation', 'Got it');
            },
          },
          disableActiveInteraction: true,
        },
      ],
    });
    navigationTour.drive();
  };

  const triggerCalculatorTour = () => {
    const onCompleteTour = () => {
      trackClick('Calculator', 'Got it');
      navigate(PATH_NAMES.calculator(), { replace: true });
    };

    const calculatorTour = driver({
      showProgress: true,
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps: [
        {
          element: '#term-lengths',
          popover: {
            title: '⏳ Choose term length',
            description: `<p>Try different term lengths to see how they affect monthly and total repayments.</p>`,
            side: 'bottom',
            align: 'center',
            nextBtnText: 'Next',
            onNextClick: () => {
              calculatorTour.moveNext();
              trackClick('Calculator', 'Next');
            },
          },
          disableActiveInteraction: true,
        },
        {
          element: '#calculator-customisation',
          popover: {
            popoverClass: 'driver-popover-custom right-aligned',
            title: '💵 Customise your loan',
            description: `<p>Enter loan amount, interest rate, and withdrawal fee - or use the sliders to test different scenarios.</p>`,
            nextBtnText: 'Next',
            side: 'right',
            align: 'center',
            onNextClick: () => {
              calculatorTour.moveNext();
              trackClick('Calculator', 'Next');
            },
          },
          disableActiveInteraction: true,
        },
        {
          element: '#repayment-schedule',
          popover: {
            popoverClass: 'driver-popover-custom left-aligned',
            title: '📅 View repayment schedule',
            description: `<p>The repayment schedule updates automatically based on your chosen term length and loan details.</p><p>You can <strong>export it as a CSV file</strong> to share with clients.</p>`,
            nextBtnText: 'Next',
            side: 'left',
            align: 'center',
            onNextClick: () => {
              calculatorTour.moveNext();
              trackClick('Calculator', 'Next');
            },
          },
          disableActiveInteraction: true,
        },
        {
          element: '#overpayment-options',
          popover: {
            popoverClass: 'driver-popover-custom right-aligned',
            title: '💡 Calculate early repayments',
            description: `<p>Tick <strong>“Calculate early repayments”</strong> to estimate repayments based on a set timeframe or amount.</p><p>The repayment schedule will update automatically.</p>`,
            nextBtnText: 'Got it',
            side: 'right',
            align: 'center',
            onNextClick: () => {
              calculatorTour.destroy();
              onCompleteTour();
            },
          },
          disableActiveInteraction: true,
        },
      ],
    });

    calculatorTour.drive();
  };

  return {
    triggerNavigationTour,
    triggerCalculatorTour,
  };
};
